import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

export type StructureType = 'zone' | 'aisle' | 'rack' | 'shelf' | 'bin';

export interface CapacityData {
  weightKg: number | null;
  lengthM: number | null;
  widthM: number | null;
  heightM: number | null;
}

interface DimensionData {
  lengthM: number | null;
  widthM: number | null;
}

export interface StructureDialogData {
  name: string;
  capacity?: CapacityData | null;
  dimensions?: DimensionData | null;
  description?: string;
  parentId?: number | null;
  zoneId?: number | null;
  aisleId?: number | null;
  maxLevelNo?: number | null;
  levelNo?: number | null;
}

@Component({
  selector: 'app-structure-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    SelectModule,
    ButtonModule,
    AppButtonComponent,
    TranslateModule,
  ],
  styles: [
    `
      ::ng-deep .p-select-overlay {
        font-size: 14px;
      }
      ::ng-deep .p-select-overlay .p-select-items-wrapper .p-select-item {
        font-size: 14px;
      }
    `,
  ],
  template: `
    <p-dialog
      data-testid="structure-dialog"
      [visible]="visibleSignal()"
      [modal]="true"
      [closable]="false"
      [style]="{ width: '34rem' }"
      [draggable]="false"
      [resizable]="false"
      (visibleChange)="handleVisibleChange($event)"
      [header]="title()"
    >
      <form
        data-testid="structure-dialog-form"
        [formGroup]="form"
        class="space-y-4"
        (ngSubmit)="onSubmit()"
      >
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">
            {{ 'logical_warehouse.structure.name' | translate }}
            <span class="text-red-500">*</span>
          </label>
          <input
            data-testid="structure-dialog-name-input"
            pInputText
            formControlName="name"
            class="w-full"
            [placeholder]="
              'logical_warehouse.structure.name_placeholder' | translate
            "
          />
          @if (
            form.get('name')?.errors?.['required'] && form.get('name')?.touched
          ) {
            <small class="text-xs text-red-500">
              {{ 'common.validation.required' | translate }}
            </small>
          }
          @if (form.get('name')?.errors?.['maxlength']) {
            <small class="text-xs text-red-500">
              {{
                'common.validation.maxlength'
                  | translate
                    : {
                        fieldName: 'logical_warehouse.structure.name' | translate,
                        requiredLength:
                          form.get('name')?.errors?.['maxlength']?.requiredLength
                      }
              }}
            </small>
          }
        </div>

        @if (typeSignal() === 'rack' && zoneOptionsSignal().length > 0) {
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">
              {{
                zoneLabel || ('logical_warehouse.structure.zone' | translate)
              }}
            </label>
            <p-select
              data-testid="structure-dialog-zone-select"
              formControlName="zoneId"
              [options]="zoneOptionsSignal()"
              optionLabel="label"
              optionValue="value"
              class="w-full text-sm"
              [showClear]="true"
              [placeholder]="
                'logical_warehouse.structure.select_zone' | translate
              "
              (onChange)="onZoneChange($event.value)"
              (onShow)="onDropdownOpen('zoneId')"
              (onHide)="onDropdownClose('zoneId')"
              appendTo="body"
            ></p-select>
          </div>
        }

        @if (typeSignal() === 'rack' && allAisleOptionsSignal().length > 0) {
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">
              {{
                aisleLabel || ('logical_warehouse.structure.aisle' | translate)
              }}
            </label>
            <p-select
              data-testid="structure-dialog-aisle-select"
              formControlName="aisleId"
              [options]="filteredAisleOptions()"
              optionLabel="label"
              optionValue="value"
              class="w-full text-sm"
              [showClear]="true"
              [placeholder]="
                'logical_warehouse.structure.select_aisle' | translate
              "
              (onShow)="onDropdownOpen('aisleId')"
              (onHide)="onDropdownClose('aisleId')"
              appendTo="body"
            ></p-select>
          </div>
        }

        @if (
          typeSignal() !== 'zone' &&
          typeSignal() !== 'rack' &&
          parentOptionsSignal().length > 0 &&
          parentLabel
        ) {
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">
              {{ parentLabel }}
              @if (typeSignal() === 'shelf' || typeSignal() === 'bin') {
                <span class="text-red-500">*</span>
              }
            </label>
            <p-select
              data-testid="structure-dialog-parent-select"
              formControlName="parentId"
              [options]="parentOptionsSignal()"
              optionLabel="label"
              optionValue="value"
              class="w-full text-sm"
              [placeholder]="parentPlaceholder()"
              appendTo="body"
              (onShow)="onDropdownOpen('parentId')"
              (onHide)="onDropdownClose('parentId')"
            ></p-select>
            @if (
              form.get('parentId')?.invalid &&
              form.get('parentId')?.touched &&
              !isDropdownOpen('parentId')
            ) {
              <small class="text-xs text-red-500">
                {{ 'common.validation.required' | translate }}
              </small>
            }
          </div>
        }

        @if (typeSignal() === 'zone' || typeSignal() === 'aisle') {
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-700">
                {{ 'logical_warehouse.structure.capacity_length' | translate }}
                <span class="text-red-500">*</span>
              </label>
              <p-inputNumber
                data-testid="structure-dialog-dimension-length-input"
                formControlName="dimensionLength"
                inputId="dimensionLength"
                class="w-full"
                [min]="0"
                [maxFractionDigits]="dimensionFractionDigits"
                [useGrouping]="true"
              ></p-inputNumber>
              @if (
                form.get('dimensionLength')?.invalid &&
                form.get('dimensionLength')?.touched
              ) {
                <small class="text-xs text-red-500">
                  {{ 'common.validation.required' | translate }}
                </small>
              }
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-700">
                {{ 'logical_warehouse.structure.capacity_width' | translate }}
                <span class="text-red-500">*</span>
              </label>
              <p-inputNumber
                data-testid="structure-dialog-dimension-width-input"
                formControlName="dimensionWidth"
                inputId="dimensionWidth"
                class="w-full"
                [min]="0"
                [maxFractionDigits]="dimensionFractionDigits"
                [useGrouping]="true"
              ></p-inputNumber>
              @if (
                form.get('dimensionWidth')?.invalid &&
                form.get('dimensionWidth')?.touched
              ) {
                <small class="text-xs text-red-500">
                  {{ 'common.validation.required' | translate }}
                </small>
              }
            </div>
          </div>
        }

        @if (
          typeSignal() === 'rack' ||
          typeSignal() === 'shelf' ||
          typeSignal() === 'bin'
        ) {
          <div class="grid grid-cols-2 gap-3">
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-700">
                {{ 'logical_warehouse.structure.capacity_weight' | translate }}
                @if (typeSignal() !== 'bin') {
                  <span class="text-red-500">*</span>
                }
              </label>
              <p-inputNumber
                data-testid="structure-dialog-capacity-weight-input"
                formControlName="capacityWeight"
                inputId="capacityWeight"
                class="w-full"
                [min]="0"
                [maxFractionDigits]="dimensionFractionDigits"
                [useGrouping]="true"
              ></p-inputNumber>
              @if (
                form.get('capacityWeight')?.invalid &&
                form.get('capacityWeight')?.touched
              ) {
                <small class="text-xs text-red-500">
                  {{ 'common.validation.required' | translate }}
                </small>
              }
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-700">
                {{ 'logical_warehouse.structure.capacity_length' | translate }}
                <span class="text-red-500">*</span>
              </label>
              <p-inputNumber
                data-testid="structure-dialog-capacity-length-input"
                formControlName="capacityLength"
                inputId="capacityLength"
                class="w-full"
                [min]="0"
                [maxFractionDigits]="dimensionFractionDigits"
                [useGrouping]="true"
              ></p-inputNumber>
              @if (
                form.get('capacityLength')?.invalid &&
                form.get('capacityLength')?.touched
              ) {
                <small class="text-xs text-red-500">
                  {{ 'common.validation.required' | translate }}
                </small>
              }
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-700">
                {{ 'logical_warehouse.structure.capacity_width' | translate }}
                <span class="text-red-500">*</span>
              </label>
              <p-inputNumber
                data-testid="structure-dialog-capacity-width-input"
                formControlName="capacityWidth"
                inputId="capacityWidth"
                class="w-full"
                [min]="0"
                [maxFractionDigits]="dimensionFractionDigits"
                [useGrouping]="true"
              ></p-inputNumber>
              @if (
                form.get('capacityWidth')?.invalid &&
                form.get('capacityWidth')?.touched
              ) {
                <small class="text-xs text-red-500">
                  {{ 'common.validation.required' | translate }}
                </small>
              }
            </div>

            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-gray-700">
                {{ 'logical_warehouse.structure.capacity_height' | translate }}
                <span class="text-red-500">*</span>
              </label>
              <p-inputNumber
                data-testid="structure-dialog-capacity-height-input"
                formControlName="capacityHeight"
                inputId="capacityHeight"
                class="w-full"
                [min]="0"
                [maxFractionDigits]="dimensionFractionDigits"
                [useGrouping]="true"
              ></p-inputNumber>
              @if (
                form.get('capacityHeight')?.invalid &&
                form.get('capacityHeight')?.touched
              ) {
                <small class="text-xs text-red-500">
                  {{ 'common.validation.required' | translate }}
                </small>
              }
            </div>
          </div>
        }

        @if (typeSignal() === 'rack') {
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">
              {{ 'logical_warehouse.structure.max_level_no' | translate }}
              <span class="text-red-500">*</span>
            </label>
            <p-inputNumber
              data-testid="structure-dialog-max-level-no-input"
              formControlName="maxLevelNo"
              inputId="maxLevelNo"
              class="w-full"
              [min]="1"
              [useGrouping]="true"
            ></p-inputNumber>
            @if (
              form.get('maxLevelNo')?.invalid && form.get('maxLevelNo')?.touched
            ) {
              <small class="text-xs text-red-500">
                {{ 'common.validation.required' | translate }}
              </small>
            }
          </div>
        }

        @if (typeSignal() === 'shelf') {
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">
              {{ 'logical_warehouse.structure.level_no' | translate }}
              <span class="text-red-500">*</span>
            </label>
            <p-inputNumber
              data-testid="structure-dialog-level-no-input"
              formControlName="levelNo"
              inputId="levelNo"
              class="w-full"
              [min]="1"
              [useGrouping]="true"
            ></p-inputNumber>
            @if (form.get('levelNo')?.invalid && form.get('levelNo')?.touched) {
              <small class="text-xs text-red-500">
                {{ 'common.validation.required' | translate }}
              </small>
            }
          </div>
        }

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-gray-700">
            {{ 'logical_warehouse.structure.description' | translate }}
          </label>
          <textarea
            data-testid="structure-dialog-description-textarea"
            pTextarea
            formControlName="description"
            rows="3"
            class="w-full text-sm"
            [placeholder]="
              'logical_warehouse.structure.description_placeholder' | translate
            "
          ></textarea>
        </div>

        <div class="flex justify-end gap-2 pt-2">
          <app-button
            testId="structure-dialog-cancel-btn"
            [label]="'common.actions.cancel' | translate"
            [type]="'secondary'"
            [outlined]="true"
            (emitOnClick)="handleVisibleChange(false)"
          ></app-button>
          <app-button
            testId="structure-dialog-save-btn"
            [label]="saveLabel()"
            [type]="'info'"
            [outlined]="false"
            (emitOnClick)="onSubmit()"
          ></app-button>
        </div>
      </form>
    </p-dialog>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StructureDialogComponent {
  readonly dimensionFractionDigits = 3;

  private translate = inject(TranslateService);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);

  @Input() set visible(value: boolean) {
    this.visibleSignal.set(!!value);
  }
  @Input() set type(value: StructureType) {
    this.typeSignal.set(value ?? 'zone');
  }
  @Input() set isEdit(value: boolean) {
    this.isEditSignal.set(!!value);
  }
  @Input() set initialData(value: StructureDialogData | null) {
    this.initialDataSignal.set(value);
  }
  @Input() set parentOptions(value: { label: string; value: number }[]) {
    this.parentOptionsSignal.set(value ?? []);
  }
  @Input() set zoneOptions(value: { label: string; value: number }[]) {
    this.zoneOptionsSignal.set(value ?? []);
  }
  @Input() set allAisleOptions(
    value: { label: string; value: number; zoneId?: number | null }[],
  ) {
    this.allAisleOptionsSignal.set(value ?? []);
  }
  @Input() parentLabel = '';
  @Input() zoneLabel = '';
  @Input() aisleLabel = '';
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<StructureDialogData>();

  visibleSignal = signal(false);
  typeSignal = signal<StructureType>('zone');
  isEditSignal = signal(false);
  title = signal('');
  saveLabel = signal('');
  initialDataSignal = signal<StructureDialogData | null>(null);
  parentOptionsSignal = signal<Array<{ label: string; value: number }>>([]);
  zoneOptionsSignal = signal<Array<{ label: string; value: number }>>([]);
  allAisleOptionsSignal = signal<
    Array<{ label: string; value: number; zoneId?: number | null }>
  >([]);
  selectedZoneForRack = signal<number | null>(null);

  // Track dropdown open state to prevent validation message from showing while dropdown is open
  private dropdownOpenState = signal<Set<string>>(new Set());

  // Track if form has been initialized to prevent re-patching on signal changes
  private formInitialized = false;

  filteredAisleOptions = computed(() => {
    const selectedZone = this.selectedZoneForRack();
    const allAisles = this.allAisleOptionsSignal();
    if (selectedZone) {
      return allAisles.filter((a) => a.zoneId === selectedZone);
    }
    return allAisles;
  });

  parentPlaceholder = computed(() => {
    const type = this.typeSignal();
    if (type === 'aisle') {
      return this.translate.instant('logical_warehouse.structure.select_zone');
    } else if (type === 'rack') {
      return this.translate.instant('logical_warehouse.structure.select_aisle');
    } else if (type === 'shelf' || type === 'bin') {
      return this.translate.instant('logical_warehouse.structure.select_rack');
    }
    return '';
  });

  form = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(255)]],
    capacityWeight: [null as number | null],
    capacityLength: [null as number | null],
    capacityWidth: [null as number | null],
    capacityHeight: [null as number | null],
    dimensionLength: [null as number | null],
    dimensionWidth: [null as number | null],
    description: [''],
    parentId: [null as number | null],
    zoneId: [null as number | null],
    aisleId: [null as number | null],
    maxLevelNo: [null as number | null],
    levelNo: [null as number | null],
  });

  constructor() {
    effect(() => {
      const currentType = this.typeSignal();
      this.title.set(
        this.translate.instant(`logical_warehouse.structure.${currentType}`),
      );
      this.saveLabel.set(
        this.translate.instant(
          this.isEditSignal() ? 'common.actions.save' : 'common.actions.add',
        ),
      );

      this.applyValidatorsForType(currentType);

      const data = this.initialDataSignal();
      const visible = this.visibleSignal();
      if (visible && data && !this.formInitialized) {
        this.form.patchValue({
          name: data.name ?? '',
          capacityWeight: data.capacity?.weightKg ?? null,
          capacityLength: data.capacity?.lengthM ?? null,
          capacityWidth: data.capacity?.widthM ?? null,
          capacityHeight: data.capacity?.heightM ?? null,
          dimensionLength: data.dimensions?.lengthM ?? null,
          dimensionWidth: data.dimensions?.widthM ?? null,
          description: data.description ?? '',
          parentId: data.parentId ?? null,
          zoneId: data.zoneId ?? null,
          aisleId: data.aisleId ?? null,
          maxLevelNo: data.maxLevelNo ?? null,
          levelNo: data.levelNo ?? null,
        });
        if (currentType === 'rack') {
          if (data.zoneId) {
            this.selectedZoneForRack.set(data.zoneId);
          } else if (data.aisleId) {
            const allAisles = this.allAisleOptionsSignal();
            const aisle = allAisles.find((a) => a.value === data.aisleId);
            if (aisle?.zoneId) {
              this.selectedZoneForRack.set(aisle.zoneId);
            }
          }
        }
        this.formInitialized = true;
      } else if (!visible) {
        this.resetForm();
        this.formInitialized = false;
      }
    });
  }

  onZoneChange(zoneId: number | null): void {
    this.selectedZoneForRack.set(zoneId);
    this.form.get('aisleId')?.setValue(null);
  }

  onDropdownOpen(fieldName: string): void {
    const current = this.dropdownOpenState();
    this.dropdownOpenState.set(new Set(current).add(fieldName));
  }

  onDropdownClose(fieldName: string): void {
    const current = this.dropdownOpenState();
    const updated = new Set(current);
    updated.delete(fieldName);
    this.dropdownOpenState.set(updated);

    // Mark control as touched when dropdown closes if it's invalid
    const control = this.form.get(fieldName);
    if (control && control.invalid) {
      control.markAsTouched();
      this.cdr.markForCheck();
    }
  }

  isDropdownOpen(fieldName: string): boolean {
    return this.dropdownOpenState().has(fieldName);
  }

  handleVisibleChange(visible: boolean): void {
    this.visibleSignal.set(visible);
    this.visibleChange.emit(visible);
    if (!visible) {
      this.resetForm();
      this.formInitialized = false;
    } else {
      // When dialog opens, patch form with initialData if available
      const data = this.initialDataSignal();
      if (data && !this.formInitialized) {
        this.form.patchValue({
          name: data.name ?? '',
          capacityWeight: data.capacity?.weightKg ?? null,
          capacityLength: data.capacity?.lengthM ?? null,
          capacityWidth: data.capacity?.widthM ?? null,
          capacityHeight: data.capacity?.heightM ?? null,
          dimensionLength: data.dimensions?.lengthM ?? null,
          dimensionWidth: data.dimensions?.widthM ?? null,
          description: data.description ?? '',
          parentId: data.parentId ?? null,
          zoneId: data.zoneId ?? null,
          aisleId: data.aisleId ?? null,
          maxLevelNo: data.maxLevelNo ?? null,
          levelNo: data.levelNo ?? null,
        });
        if (this.typeSignal() === 'rack') {
          if (data.zoneId) {
            this.selectedZoneForRack.set(data.zoneId);
          } else if (data.aisleId) {
            const allAisles = this.allAisleOptionsSignal();
            const aisle = allAisles.find((a) => a.value === data.aisleId);
            if (aisle?.zoneId) {
              this.selectedZoneForRack.set(aisle.zoneId);
            }
          }
        }
        this.formInitialized = true;
      }
    }
  }

  private resetForm(): void {
    this.form.reset({
      name: '',
      capacityWeight: null,
      capacityLength: null,
      capacityWidth: null,
      capacityHeight: null,
      dimensionLength: null,
      dimensionWidth: null,
      description: '',
      parentId: null,
      zoneId: null,
      aisleId: null,
      maxLevelNo: null,
      levelNo: null,
    });
    this.selectedZoneForRack.set(null);
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }
    const type = this.typeSignal();
    const payload: StructureDialogData = {
      name: this.form.value.name ?? '',
      description: this.form.value.description ?? '',
      parentId: this.form.value.parentId ?? null,
    };

    if (type === 'rack') {
      payload.zoneId = this.form.value.zoneId ?? null;
      payload.aisleId = this.form.value.aisleId ?? null;
      payload.maxLevelNo = this.form.value.maxLevelNo ?? null;
    }

    if (type === 'shelf') {
      payload.levelNo = this.form.value.levelNo ?? null;
    }

    if (type === 'zone' || type === 'aisle') {
      payload.dimensions = {
        lengthM: this.form.value.dimensionLength ?? null,
        widthM: this.form.value.dimensionWidth ?? null,
      };
    } else {
      payload.capacity = {
        weightKg: this.form.value.capacityWeight ?? null,
        lengthM: this.form.value.capacityLength ?? null,
        widthM: this.form.value.capacityWidth ?? null,
        heightM: this.form.value.capacityHeight ?? null,
      };
    }

    this.confirm.emit(payload);
    this.handleVisibleChange(false);
  }

  private applyValidatorsForType(type: StructureType): void {
    const capControls = [
      this.form.get('capacityWeight'),
      this.form.get('capacityLength'),
      this.form.get('capacityWidth'),
      this.form.get('capacityHeight'),
    ];
    const dimLength = this.form.get('dimensionLength');
    const dimWidth = this.form.get('dimensionWidth');
    const parent = this.form.get('parentId');
    const maxLevelNo = this.form.get('maxLevelNo');
    const levelNo = this.form.get('levelNo');

    capControls.forEach((control) => {
      control?.clearValidators();
      control?.updateValueAndValidity({ emitEvent: false });
    });
    dimLength?.clearValidators();
    dimWidth?.clearValidators();
    dimLength?.updateValueAndValidity({ emitEvent: false });
    dimWidth?.updateValueAndValidity({ emitEvent: false });
    parent?.clearValidators();
    parent?.updateValueAndValidity({ emitEvent: false });
    maxLevelNo?.clearValidators();
    maxLevelNo?.updateValueAndValidity({ emitEvent: false });
    levelNo?.clearValidators();
    levelNo?.updateValueAndValidity({ emitEvent: false });

    if (type === 'zone' || type === 'aisle') {
      dimLength?.addValidators([Validators.required, Validators.min(0)]);
      dimWidth?.addValidators([Validators.required, Validators.min(0)]);
      dimLength?.updateValueAndValidity({ emitEvent: false });
      dimWidth?.updateValueAndValidity({ emitEvent: false });
      // Aisle parent optional (root or zone)
    } else if (type === 'rack' || type === 'shelf') {
      capControls.forEach((control) => {
        control?.addValidators([Validators.required, Validators.min(0)]);
        control?.updateValueAndValidity({ emitEvent: false });
      });
      // Rack parent optional (aisle/zone/root). Shelf still needs parent.
      if (type === 'rack') {
        maxLevelNo?.addValidators([Validators.required, Validators.min(1)]);
        maxLevelNo?.updateValueAndValidity({ emitEvent: false });
      }
      if (type === 'shelf') {
        parent?.addValidators([Validators.required]);
        parent?.updateValueAndValidity({ emitEvent: false });
        levelNo?.addValidators([Validators.required, Validators.min(1)]);
        levelNo?.updateValueAndValidity({ emitEvent: false });
      }
    } else if (type === 'bin') {
      this.form
        .get('capacityLength')
        ?.addValidators([Validators.required, Validators.min(0)]);
      this.form
        .get('capacityWidth')
        ?.addValidators([Validators.required, Validators.min(0)]);
      this.form
        .get('capacityHeight')
        ?.addValidators([Validators.required, Validators.min(0)]);
      this.form
        .get('capacityLength')
        ?.updateValueAndValidity({ emitEvent: false });
      this.form
        .get('capacityWidth')
        ?.updateValueAndValidity({ emitEvent: false });
      this.form
        .get('capacityHeight')
        ?.updateValueAndValidity({ emitEvent: false });
      // Only require parent when adding new bin, not when editing
      if (!this.isEditSignal()) {
        parent?.addValidators([Validators.required]);
        parent?.updateValueAndValidity({ emitEvent: false });
      }
    }
  }
}
